/**
 * UsageByProviderChart Component
 *
 * Displays a pie/donut chart showing token usage breakdown by AI provider.
 * Uses simple CSS-based visualization (no external chart library required).
 *
 * Technical Design Reference: docs/technical/TD-AC-594-token-audit.md
 * Related Tickets:
 * - AC-594 (Epic: Token Audit System)
 * - AC-608 (Frontend: UsageByProviderChart component)
 * - AC-612 (Parent: UsageDashboard container)
 */

import React from 'react';
import { Building2, AlertCircle } from 'lucide-react';
import type { TokenUsageByProvider } from '../../types/tokenUsage';

export interface UsageByProviderChartProps {
  /** Token usage by provider data from API */
  data: TokenUsageByProvider[] | null;
  /** Whether data is currently loading */
  loading?: boolean;
  /** Error message to display */
  error?: string | null;
}

/**
 * Format a number with thousands separator using locale formatting.
 */
const formatNumber = (n: number): string => {
  return n.toLocaleString('en-US');
};

/**
 * Format a number as currency with $ symbol.
 * Uses adaptive precision: 4 decimal places for amounts under $1,
 * 2 decimal places for larger amounts.
 */
const formatCurrency = (n: number): string => {
  const decimalPlaces = n < 1 && n > 0 ? 4 : 2;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces })}`;
};

/**
 * Provider color configuration for consistent visual representation.
 */
const providerColors: Record<string, { bg: string; text: string; ring: string }> = {
  openai: { bg: 'bg-green-500', text: 'text-green-600', ring: 'ring-green-500' },
  anthropic: { bg: 'bg-orange-500', text: 'text-orange-600', ring: 'ring-orange-500' },
  google: { bg: 'bg-blue-500', text: 'text-primary-600', ring: 'ring-blue-500' },
  other: { bg: 'bg-gray-500', text: 'text-gray-600 dark:text-gray-400', ring: 'ring-gray-500' },
};

/**
 * Get color configuration for a provider.
 */
const getProviderColors = (provider: string): { bg: string; text: string; ring: string } => {
  return providerColors[provider.toLowerCase()] || providerColors.other;
};

/**
 * Get provider display name with proper capitalization.
 */
const getProviderDisplayName = (provider: string): string => {
  const displayNames: Record<string, string> = {
    openai: 'OpenAI',
    anthropic: 'Anthropic',
    google: 'Google',
  };
  return displayNames[provider.toLowerCase()] || provider;
};

/**
 * Loading skeleton for the chart.
 */
const LoadingSkeleton: React.FC = () => (
  <div
    role="status"
    aria-busy="true"
    aria-label="Loading provider usage data"
    data-testid="provider-chart-loading"
    className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 animate-pulse"
  >
    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-40 mb-4" />
    <div className="flex items-center space-x-8">
      <div className="w-32 h-32 bg-gray-200 dark:bg-gray-700 rounded-full" />
      <div className="flex-1 space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center space-x-3">
            <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="w-20 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="w-12 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

/**
 * Error state component.
 */
const ErrorState: React.FC<{ message: string }> = ({ message }) => (
  <div
    role="alert"
    data-testid="provider-chart-error"
    className="bg-white dark:bg-cap-dark rounded-lg shadow p-6"
  >
    <div className="flex items-center text-red-600 mb-4">
      <AlertCircle data-testid="provider-error-icon" className="w-5 h-5 mr-2" />
      <span className="font-medium">Error loading provider data</span>
    </div>
    <p className="text-gray-600 dark:text-gray-400">{message}</p>
  </div>
);

/**
 * Empty state when no data is available.
 */
const EmptyState: React.FC = () => (
  <div
    data-testid="provider-chart-empty"
    className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 text-center"
  >
    <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
    <p className="text-gray-500 dark:text-gray-400">No provider usage data available</p>
    <p className="text-sm text-gray-400 mt-1">Data will appear once AI providers are used</p>
  </div>
);

/**
 * Calculate conic gradient for pie chart visualization.
 */
const calculateConicGradient = (data: TokenUsageByProvider[]): string => {
  if (data.length === 0) return 'conic-gradient(#e5e7eb 0deg 360deg)';

  const sortedData = [...data].sort((a, b) => b.percentage_of_total - a.percentage_of_total);
  let currentAngle = 0;
  const gradientParts: string[] = [];

  sortedData.forEach((provider) => {
    const colors = getProviderColors(provider.provider);
    const bgColor = colors.bg.replace('bg-', '');
    // Map Tailwind color names to actual CSS colors
    const colorMap: Record<string, string> = {
      'green-500': '#22c55e',
      'orange-500': '#f97316',
      'blue-500': '#3b82f6',
      'gray-500': '#6b7280',
    };
    const cssColor = colorMap[bgColor] || '#6b7280';

    const angle = (provider.percentage_of_total / 100) * 360;
    gradientParts.push(`${cssColor} ${currentAngle}deg ${currentAngle + angle}deg`);
    currentAngle += angle;
  });

  return `conic-gradient(${gradientParts.join(', ')})`;
};

/**
 * UsageByProviderChart component displays token usage breakdown by AI provider.
 */
const UsageByProviderChart: React.FC<UsageByProviderChartProps> = ({
  data,
  loading = false,
  error = null,
}) => {
  // Show loading state
  if (loading) {
    return <LoadingSkeleton />;
  }

  // Show error state
  if (error) {
    return <ErrorState message={error} />;
  }

  // Show empty state when no data
  if (!data || data.length === 0) {
    return <EmptyState />;
  }

  // Sort by percentage descending
  const sortedData = [...data].sort((a, b) => b.percentage_of_total - a.percentage_of_total);

  // Calculate totals
  const totalTokens = data.reduce((sum, d) => sum + d.total_tokens, 0);
  const totalCost = data.reduce((sum, d) => sum + d.total_cost, 0);
  const totalCalls = data.reduce((sum, d) => sum + d.api_call_count, 0);

  return (
    <div
      data-testid="usage-by-provider-chart"
      className="bg-white dark:bg-cap-dark rounded-lg shadow p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Building2 data-testid="provider-chart-icon" className="w-5 h-5 text-primary-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">Usage by Provider</h3>
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400">{data.length} provider{data.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Chart and Legend */}
      <div className="flex items-center space-x-8">
        {/* Pie Chart (CSS-based donut) */}
        <div
          data-testid="provider-pie-chart"
          className="relative"
        >
          <div
            className="w-32 h-32 rounded-full"
            style={{
              background: calculateConicGradient(sortedData),
            }}
          >
            {/* Inner circle to create donut effect */}
            <div className="absolute inset-4 bg-white dark:bg-cap-dark rounded-full flex items-center justify-center">
              <div className="text-center">
                <div className="text-lg font-bold text-gray-900 dark:text-warm-white">{data.length}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Providers</div>
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div
          data-testid="provider-chart-legend"
          className="flex-1 space-y-3"
        >
          {sortedData.map((provider, index) => {
            const colors = getProviderColors(provider.provider);
            return (
              <div
                key={provider.provider}
                data-testid={`provider-legend-${index}`}
                className="flex items-center justify-between group cursor-default"
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded ${colors.bg}`} />
                  <span className="font-medium text-gray-900 dark:text-warm-white">
                    {getProviderDisplayName(provider.provider)}
                  </span>
                </div>
                <div className="flex items-center space-x-4 text-sm">
                  <span className="text-gray-500 dark:text-gray-400">{provider.percentage_of_total.toFixed(1)}%</span>
                  <span className="font-medium text-gray-900 dark:text-warm-white">{formatCurrency(provider.total_cost)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detailed breakdown */}
      <div
        data-testid="provider-chart-details"
        className="mt-6 space-y-3"
      >
        {sortedData.map((provider, index) => {
          const colors = getProviderColors(provider.provider);
          return (
            <div
              key={provider.provider}
              data-testid={`provider-detail-${index}`}
              className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded ${colors.bg}`} />
                  <span className="font-medium text-gray-900 dark:text-warm-white">
                    {getProviderDisplayName(provider.provider)}
                  </span>
                </div>
                <span className={`font-bold ${colors.text}`}>
                  {provider.percentage_of_total.toFixed(1)}%
                </span>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-gray-600 dark:text-gray-400">
                <div>
                  <span className="text-gray-400">Tokens:</span>{' '}
                  {formatNumber(provider.total_tokens)}
                </div>
                <div>
                  <span className="text-gray-400">Cost:</span>{' '}
                  {formatCurrency(provider.total_cost)}
                </div>
                <div>
                  <span className="text-gray-400">Calls:</span>{' '}
                  {formatNumber(provider.api_call_count)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div
        data-testid="provider-chart-summary"
        className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 grid grid-cols-3 gap-4 text-sm"
      >
        <div>
          <span className="text-gray-500 dark:text-gray-400">Total Tokens:</span>
          <span className="ml-2 font-medium text-gray-900 dark:text-warm-white">{formatNumber(totalTokens)}</span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">Total Cost:</span>
          <span className="ml-2 font-medium text-gray-900 dark:text-warm-white">{formatCurrency(totalCost)}</span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">Total Calls:</span>
          <span className="ml-2 font-medium text-gray-900 dark:text-warm-white">{formatNumber(totalCalls)}</span>
        </div>
      </div>
    </div>
  );
};

export default UsageByProviderChart;
